// Cvanto Shopping Profile (web, §41) — the private place for sizes, style and
// preferences Cvanto shops with. Same backend, same data and same privacy model
// as the mobile app: GET/PUT/DELETE /api/shopping-profile (owner session only).
// Functional and responsive; the mobile app carries the richer motion.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Check } from "lucide-react";
import { getAccessToken } from "../../../awsAuth";
import {
  AUDIENCE_LABELS,
  COLOR_SWATCH,
  FIT_LABELS,
  JEANS_WAIST,
  NUMERIC_CLOTHING,
  POPULAR_BRANDS,
  RISE_LABELS,
  SHOE_EU,
  STEP_ORDER,
  STEP_TITLES,
  STYLE_LABELS,
  fromProfile,
  isDraftEmpty,
  sameDraft,
  toPayload,
  toggle,
  validateDraft,
} from "./shoppingProfileModel";
import "./ShoppingProfile.scss";

const API_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API || "http://localhost:5000";

async function call(method, body) {
  const token = await getAccessToken().catch(() => null);
  const res = await fetch(`${API_BASE}/api/shopping-profile`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Не вдалося виконати запит");
    err.fieldErrors = data?.errors || [];
    throw err;
  }
  return data;
}

function Chip({ label, selected, onClick, dot }) {
  return (
    <button type="button" className={`spChip${selected ? " is-on" : ""}`} aria-pressed={selected} onClick={onClick}>
      {dot ? <span className="spChip__dot" style={{ background: dot }} /> : null}
      {label}
    </button>
  );
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="spSeg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={`spSeg__item${value === o.id ? " is-on" : ""}`} onClick={() => onChange(value === o.id ? null : o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function NumberField({ label, unit, value, onChange, error, placeholder }) {
  return (
    <label className="spField">
      <span className="spField__label">{label}</span>
      <span className={`spField__box${error ? " has-error" : ""}`}>
        <input inputMode="decimal" value={value} placeholder={placeholder} maxLength={7} onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ""))} aria-label={`${label}, ${unit}`} />
        <span className="spField__unit">{unit}</span>
      </span>
      {error ? <span className="spField__error">{error}</span> : null}
    </label>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="spCard">
      <h2>{title}</h2>
      {hint ? <p className="spCard__hint">{hint}</p> : null}
      {children}
    </section>
  );
}

function Label({ children, hint }) {
  return (
    <div className="spLabel">
      <span>{children}</span>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

export default function ShoppingProfile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [brandText, setBrandText] = useState("");
  const [avoidBrandText, setAvoidBrandText] = useState("");

  const apply = useCallback((next) => {
    setData(next);
    const d = fromProfile(next.profile, next.accountAudience);
    setDraft(d);
    setSaved(d);
  }, []);

  const load = useCallback(async () => {
    setError("");
    try {
      apply(await call("GET"));
    } catch (e) {
      setError(e.message || "Не вдалося завантажити");
    }
  }, [apply]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
    setMessage("");
  };

  const dirty = useMemo(() => !!draft && !!saved && !sameDraft(draft, saved), [draft, saved]);
  const vocab = data?.vocab;
  const done = data?.completeness?.done ?? 0;
  const total = data?.completeness?.total ?? STEP_ORDER.length;

  const save = async () => {
    const found = validateDraft(draft, vocab?.ranges);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    if (isDraftEmpty(draft)) {
      setMessage("Нічого зберігати. Щоб видалити все, скористайтесь «Очистити Shopping Profile».");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      apply(await call("PUT", { profile: toPayload(draft) }));
      setMessage("Збережено");
    } catch (e) {
      setMessage(e.fieldErrors?.length ? "Деякі значення поза допустимими межами." : e.message || "Не вдалося зберегти.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Очистити Shopping Profile? Розміри, мірки, стиль і вподобання буде видалено, AI-пошук перестане їх враховувати.")) return;
    setSaving(true);
    try {
      apply(await call("DELETE"));
      setMessage("Shopping Profile очищено");
    } catch (e) {
      setMessage(e.message || "Не вдалося очистити.");
    } finally {
      setSaving(false);
    }
  };

  const addBrand = (kind, raw) => {
    const name = raw.trim();
    if (!name) return;
    const key = kind === "like" ? "likeBrands" : "avoidBrands";
    if (!draft[key].some((b) => b.toLowerCase() === name.toLowerCase())) set(key, [...draft[key], name].slice(0, vocab?.limits?.brands ?? 12));
    if (kind === "like") setBrandText("");
    else setAvoidBrandText("");
  };

  return (
    <div className="spPage">
      <div className="spWrap">
        <header className="spHead">
          <button type="button" className="spBack" onClick={() => navigate(-1)} aria-label="Назад">
            <ArrowLeft size={18} />
          </button>
          <h1>Shopping Profile</h1>
        </header>

        {error && !data ? (
          <div className="spState">
            <p>{error}</p>
            <button type="button" className="spBtn" onClick={load}>Спробувати ще раз</button>
          </div>
        ) : !draft ? (
          <div className="spState" aria-busy="true">Завантаження…</div>
        ) : (
          <>
            <section className="spHero">
              <span className="spLock"><Lock size={12} /> Приватно</span>
              <h2>Щоб Cvanto шукав для вас</h2>
              <p>Розміри, стиль і вподобання для персональних рекомендацій. Бачите їх лише ви. AI-пошук ставить першими речі у вашому розмірі, кольорах і стилі, нічого не ховаючи; те, що ви просите зараз, завжди важливіше за профіль.</p>
              <div className="spSegs" aria-label={`Заповнено ${done} з ${total}`}>
                {STEP_ORDER.map((s) => (
                  <i key={s} className={data.completeness.groups[s] ? "is-on" : ""} title={STEP_TITLES[s]} />
                ))}
              </div>
              <small>{done === 0 ? "Займає хвилину. Усе необов’язково." : done === total ? "Усе заповнено" : `Заповнено ${done} з ${total}`}</small>
            </section>

            <Section title="Про вас">
              <Label hint={data.accountAudience ? "За замовчуванням — стать з вашого профілю." : undefined}>Для кого шукаємо</Label>
              <Segmented label="Для кого шукаємо" options={vocab.audiences.map((a) => ({ id: a, label: AUDIENCE_LABELS[a] }))} value={draft.audience} onChange={(v) => set("audience", v)} />
            </Section>

            <Section title="Розміри" hint="Позначте лише те, що знаєте.">
              <Label>Верх: футболки, куртки, сукні</Label>
              <div className="spWrapChips">{vocab.letterSizes.map((s) => <Chip key={s} label={s.toUpperCase()} selected={draft.top === s} onClick={() => set("top", draft.top === s ? null : s)} />)}</div>
              <Label>Низ: штани, спідниці</Label>
              <div className="spWrapChips">{vocab.letterSizes.map((s) => <Chip key={s} label={s.toUpperCase()} selected={draft.bottom === s} onClick={() => set("bottom", draft.bottom === s ? null : s)} />)}</div>
              <Label hint="Якщо звикли до числових розмірів (EU).">Числовий розмір</Label>
              <div className="spWrapChips">{NUMERIC_CLOTHING.map((n) => <Chip key={n} label={String(n)} selected={draft.numeric === n} onClick={() => set("numeric", draft.numeric === n ? null : n)} />)}</div>
              <Label hint="Обхват талії в дюймах, як у W28 / W32.">Джинси</Label>
              <div className="spWrapChips">{JEANS_WAIST.map((n) => <Chip key={n} label={`W${n}`} selected={draft.jeansWaist === n} onClick={() => set("jeansWaist", draft.jeansWaist === n ? null : n)} />)}</div>
              <Label>Взуття (EU)</Label>
              <div className="spWrapChips">{SHOE_EU.map((n) => <Chip key={n} label={String(n)} selected={draft.shoeEu === n} onClick={() => set("shoeEu", draft.shoeEu === n ? null : n)} />)}</div>
              <div className="spRow"><NumberField label="Довжина стопи" unit="см" value={draft.footLengthCm} onChange={(t) => set("footLengthCm", t)} error={errors.footLengthCm} placeholder="24,5" /></div>
            </Section>

            <Section title="Параметри" hint="Тільки для вас: не показуються іншим і не передаються AI-моделі. Усе необов’язково.">
              <div className="spRow">
                <NumberField label="Зріст" unit="см" value={draft.heightCm} onChange={(t) => set("heightCm", t)} error={errors.heightCm} placeholder="170" />
                <NumberField label="Вага" unit="кг" value={draft.weightKg} onChange={(t) => set("weightKg", t)} error={errors.weightKg} placeholder="60" />
              </div>
              <div className="spRow">
                <NumberField label="Груди" unit="см" value={draft.chestCm} onChange={(t) => set("chestCm", t)} error={errors.chestCm} placeholder="90" />
                <NumberField label="Талія" unit="см" value={draft.waistCm} onChange={(t) => set("waistCm", t)} error={errors.waistCm} placeholder="70" />
                <NumberField label="Стегна" unit="см" value={draft.hipsCm} onChange={(t) => set("hipsCm", t)} error={errors.hipsCm} placeholder="95" />
              </div>
            </Section>

            <Section title="Стиль">
              <Label hint="Можна обрати кілька.">Ваш стиль</Label>
              <div className="spWrapChips">{vocab.styles.map((s) => <Chip key={s} label={STYLE_LABELS[s] || s} selected={draft.styles.includes(s)} onClick={() => set("styles", toggle(draft.styles, s))} />)}</div>
              <Label>Крій верху</Label>
              <Segmented label="Крій верху" options={vocab.fits.map((f) => ({ id: f, label: FIT_LABELS[f] || f }))} value={draft.fitTop} onChange={(v) => set("fitTop", v)} />
              <Label>Крій низу</Label>
              <Segmented label="Крій низу" options={vocab.fits.map((f) => ({ id: f, label: FIT_LABELS[f] || f }))} value={draft.fitBottom} onChange={(v) => set("fitBottom", v)} />
              <Label>Посадка штанів</Label>
              <Segmented label="Посадка штанів" options={vocab.rises.map((r) => ({ id: r, label: RISE_LABELS[r] || r }))} value={draft.rise} onChange={(v) => set("rise", v)} />
            </Section>

            <Section title="Вподобання">
              <Label hint="Такі речі показуватимемо першими.">Улюблені кольори</Label>
              <div className="spWrapChips">
                {vocab.colors.map((c) => (
                  <Chip
                    key={c}
                    label={c.charAt(0).toUpperCase() + c.slice(1)}
                    dot={COLOR_SWATCH[c]}
                    selected={draft.likeColors.includes(c)}
                    onClick={() => {
                      set("likeColors", toggle(draft.likeColors, c, vocab.limits.colors));
                      if (draft.avoidColors.includes(c)) set("avoidColors", draft.avoidColors.filter((x) => x !== c));
                    }}
                  />
                ))}
              </div>
              <Label>Кольори, яких уникаєте</Label>
              <div className="spWrapChips">
                {vocab.colors.filter((c) => !draft.likeColors.includes(c)).map((c) => (
                  <Chip key={c} label={c.charAt(0).toUpperCase() + c.slice(1)} dot={COLOR_SWATCH[c]} selected={draft.avoidColors.includes(c)} onClick={() => set("avoidColors", toggle(draft.avoidColors, c, vocab.limits.colors))} />
                ))}
              </div>

              <Label hint="Додайте свої або оберіть нижче.">Улюблені бренди</Label>
              <form className="spBrand" onSubmit={(e) => { e.preventDefault(); addBrand("like", brandText); }}>
                <input value={brandText} onChange={(e) => setBrandText(e.target.value)} placeholder="Напр. Zara" maxLength={40} aria-label="Улюблений бренд" />
                <button type="submit" className="spBtn spBtn--small" disabled={!brandText.trim()}>Додати</button>
              </form>
              <div className="spWrapChips">
                {draft.likeBrands.map((b) => <Chip key={b} label={`${b} ✕`} selected onClick={() => set("likeBrands", draft.likeBrands.filter((x) => x !== b))} />)}
                {POPULAR_BRANDS.filter((b) => !draft.likeBrands.some((x) => x.toLowerCase() === b.toLowerCase())).map((b) => <Chip key={b} label={b} selected={false} onClick={() => addBrand("like", b)} />)}
              </div>

              <Label>Бренди, яких уникаєте</Label>
              <form className="spBrand" onSubmit={(e) => { e.preventDefault(); addBrand("avoid", avoidBrandText); }}>
                <input value={avoidBrandText} onChange={(e) => setAvoidBrandText(e.target.value)} placeholder="Бренд" maxLength={40} aria-label="Бренд, якого уникаєте" />
                <button type="submit" className="spBtn spBtn--small" disabled={!avoidBrandText.trim()}>Додати</button>
              </form>
              <div className="spWrapChips">
                {draft.avoidBrands.map((b) => <Chip key={b} label={`${b} ✕`} selected onClick={() => set("avoidBrands", draft.avoidBrands.filter((x) => x !== b))} />)}
              </div>

              <Label hint="Верхня межа для типової покупки. Це підказка, не фільтр.">Звичайний бюджет</Label>
              <div className="spRow"><NumberField label="До" unit="грн" value={draft.budgetMax} onChange={(t) => set("budgetMax", t)} error={errors.budgetMax} placeholder="3000" /></div>
            </Section>

            {data.profile ? (
              <button type="button" className="spClear" onClick={clear} disabled={saving}>Очистити Shopping Profile</button>
            ) : null}
          </>
        )}
      </div>

      {draft ? (
        <div className="spBar">
          <div className="spBar__inner">
            <span className="spBar__msg" role="status">
              {message === "Збережено" ? <><Check size={14} /> Збережено</> : message || (dirty ? "Є незбережені зміни" : "")}
            </span>
            <button type="button" className="spBtn" onClick={save} disabled={!dirty || saving}>{saving ? "Збереження…" : "Зберегти"}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
